import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import {
  cardActive,
  cardButtonActive,
  cardButtonDefault,
  cardButtonEditing,
  cardButtonFault,
  cardDefault,
  cardEditing,
  cardFault,
  cardInnerArea,
  iconContainerActive,
  iconContainerDefault,
  iconContainerEditing,
  iconContainerFault,
  radiusCard,
  radiusCardInner,
  radiusPill,
  radiusSection,
} from 'shared/styles/commonStyles';
import { selectUnits } from 'shared/slice/unitsSlice';
import { selectUserPermissions } from 'shared/slice/userSlice';

import { convertCelsiusToFahrenheit } from 'shared/helpers/helpers';
import ConflictMessage from 'features/masterControl/components/userMessages/ConflictMessage';
import InputTempMessage from 'shared/components/userMessages/inputTempMessage';
import ScheduleCalendar from 'features/masterControl/components/controls/heatingScheduler/ScheduleCalendar';
import { CONTROL_BOX_CONFIG, MOBILE_PROGRAMS } from './ControlBox/controlBoxConfig';
import { useControlHandlers } from './ControlBox/useControlHandlers';

const PROGRAM_META = {
  instantHeat: {
    label: 'Instant Heat Program',
    icon: '/images/logo-instantHeat.svg',
    paramLabel: 'Target Temperature',
    hasTemp: true,
    actionLabel: 'Edit Parameters',
    hint: 'Update the next target temperature before activation.',
  },
  snowSensor: {
    label: 'Snow Sensor Program',
    icon: '/images/logo-snowSensor.svg',
    paramLabel: 'Default Temperature',
    hasTemp: true,
    actionLabel: '',
    hint: 'Default trigger temperature is managed in settings.',
  },
  optionalConstantTemp: {
    label: 'Optional Constant Temperature',
    icon: '/images/logo-constantTemp.svg',
    paramLabel: 'Target Temperature',
    hasTemp: true,
    actionLabel: 'Edit Parameters',
    hint: 'Stage a holding temperature for steady operation.',
  },
  heatingSchedule: {
    label: 'Heating Schedule',
    icon: '/images/logo-schedule.svg',
    paramLabel: 'Schedule Temperature',
    hasTemp: true,
    actionLabel: 'Edit Schedule',
    hint: 'Schedule timing and threshold before the next run.',
  },
  windFactor: {
    label: 'Wind Factor',
    icon: '/images/logo-windFactor.svg',
    paramLabel: 'Status',
    hasTemp: false,
    actionLabel: '',
    hint: 'Wind factor runs directly from the program toggle.',
  },
  fanOnly: {
    label: 'Fan Only',
    icon: '/images/tgs-fanOnly.svg',
    paramLabel: 'Fan Status',
    hasTemp: false,
    actionLabel: '',
    hint: 'Fan mode toggles directly and has no extra parameters.',
  },
};

const CARD_STATE = {
  DEFAULT: 'default',
  EDITING: 'editing',
  ACTIVE: 'active',
  FAULT: 'fault',
  DISABLED: 'disabled',
};

const STATE_LABEL = {
  [CARD_STATE.DEFAULT]: 'Standby',
  [CARD_STATE.EDITING]: 'Ready',
  [CARD_STATE.ACTIVE]: 'Active',
  [CARD_STATE.FAULT]: 'Fault',
  [CARD_STATE.DISABLED]: 'Disabled',
};

const STATE_ACCENT = {
  [CARD_STATE.DEFAULT]: '#64748b',
  [CARD_STATE.EDITING]: '#3b82f6',
  [CARD_STATE.ACTIVE]: '#00d492',
  [CARD_STATE.FAULT]: '#ee4b56',
  [CARD_STATE.DISABLED]: '#475569',
};

// programs whose old controller turns off on `isActivated || isReady`, so being
// ready means armed and the toggle has to read as on.
// heatingSchedule is deliberately absent: its isReady only means a schedule with
// a temperature exists, and its old button opens the calendar rather than
// turning anything off — treating it as armed pinned the toggle on for good
const READY_MEANS_ARMED = ['instantHeat', 'snowSensor', 'optionalConstantTemp', 'windFactor'];

// a program is only running when the device says so too, otherwise every card
// the user has ever armed would read as active at the same time
const PROGRAM_MODE = {
  instantHeat: 'SWITCH',
  snowSensor: 'SNOW',
  heatingSchedule: 'SCHEDULE',
  windFactor: 'WIND',
};

const TEMP_PROGRAM_KEY_MAP = {
  instantHeat: 'instantHeat',
  optionalConstantTemp: 'optionalConstantTemp',
};

const getProgramTempLimits = (programKey, isF) => {
  if (programKey === 'optionalConstantTemp') {
    return isF ? { min: 77, max: 248 } : { min: 25, max: 120 };
  }

  return isF ? { min: 250, max: 1830 } : { min: 121, max: 999 };
};

const SwitchProgramCards = ({ swtName, location, machine }) => {
  const config = CONTROL_BOX_CONFIG[swtName];
  const {
    switchSelector,
    flatSwitchKey,
    hasOptionalConstant,
    hasFanOnly,
    hasConflictMessage,
  } = config;

  const switchData = useSelector(switchSelector);
  const flatSwitch = switchData[flatSwitchKey];
  const switchStatus = flatSwitch?.[location]?.[machine];

  const { isF } = useSelector(selectUnits);
  const permissions = useSelector(selectUserPermissions);
  const disabled = !permissions.WRITE;
  const unit = isF ? '°F' : '°C';

  const {
    integratedButtonHandler,
    openMessageBox,
    setOpenMessageBox,
    message,
    programName,
    editingProgramKey,
    showScheduleEditor,
    setShowScheduleEditor,
    handleCancelConflictMessage,
    handleConfirmConflictMessage,
  } = useControlHandlers({ swtName, location, machine });

  const [scheduleTempInput, setScheduleTempInput] = useState('');
  const [scheduleData, setScheduleData] = useState(null);

  if (!switchStatus) return null;

  const {
    isFaults,
    isOff,
    instantHeat,
    snowSensor,
    heatingSchedule,
    heatingScheduleList,
    windFactor,
    isDisabled,
    isFanDisabled,
    displayConflictMessage,
    devicesConflicts,
    machineMode,
  } = switchStatus;

  const optionalConstantTemp = hasOptionalConstant ? switchStatus.optionalConstantTemp : null;
  const fanOnly = hasFanOnly ? switchStatus.fanOnly : null;
  const ebpLocked = switchStatus.EBP_mode === 0 && switchStatus.isEbp;
  const controlsDisabled = isOff || isDisabled || disabled || ebpLocked;
  const programs = MOBILE_PROGRAMS[swtName] || [];

  const visiblePrograms = programs.filter(({ key }) => {
    if (key === 'optionalConstantTemp' && !hasOptionalConstant) return false;
    if (key === 'fanOnly' && !hasFanOnly) return false;
    return !!PROGRAM_META[key];
  });

  // the flag the user set, regardless of what the device is currently running
  const getProgramArmed = (key) => {
    switch (key) {
      case 'instantHeat':
        return Boolean(instantHeat?.isActivated);
      case 'snowSensor':
        return Boolean(snowSensor?.isActivated);
      case 'optionalConstantTemp':
        return Boolean(optionalConstantTemp?.isActivated);
      case 'heatingSchedule':
        return Boolean(heatingSchedule?.isActivated);
      case 'windFactor':
        return Boolean(windFactor?.isActivated);
      case 'fanOnly':
        return Boolean(fanOnly);
      default:
        return false;
    }
  };

  const getProgramActive = (key) => {
    const mode = PROGRAM_MODE[key];
    // optionalConstantTemp and fanOnly have no mode of their own — the slice
    // already folds the device mode into their isActivated flag
    if (!mode) return getProgramArmed(key);
    return getProgramArmed(key) && machineMode === mode;
  };

  const getProgramReady = (key) => {
    switch (key) {
      case 'instantHeat':
        return instantHeat?.isReady;
      case 'snowSensor':
        return snowSensor?.isReady;
      case 'optionalConstantTemp':
        return optionalConstantTemp?.isReady;
      case 'heatingSchedule':
        return heatingSchedule?.isReady;
      case 'windFactor':
        return windFactor?.isReady;
      default:
        return false;
    }
  };

  const getCardState = (key) => {
    // an ebp-locked, inactive or unwritable switch renders every controller as
    // disabled in the old app, and that look wins over the program's own state
    if (isProgramDisabled(key)) return CARD_STATE.DISABLED;
    // a switch fault belongs to the programs that were turned on, not to every
    // card on the panel — an idle program still reads as standby
    if (isFaults && getProgramArmed(key)) return CARD_STATE.FAULT;
    if (getProgramActive(key)) return CARD_STATE.ACTIVE;
    if (getProgramReady(key)) return CARD_STATE.EDITING;
    return CARD_STATE.DEFAULT;
  };

  const getTemp = (key) => {
    switch (key) {
      case 'instantHeat':
        return instantHeat?.inputTemp > 0 ? `${instantHeat.inputTemp} ${unit}` : `--- ${unit}`;
      case 'snowSensor': {
        if (snowSensor?.defaultTemp > 0) {
          const temp = isF
            ? convertCelsiusToFahrenheit(snowSensor.defaultTemp)
            : snowSensor.defaultTemp;
          return `${temp} ${unit}`;
        }
        return `--- ${unit}`;
      }
      case 'optionalConstantTemp':
        return optionalConstantTemp?.inputTemp > 0
          ? `${optionalConstantTemp.inputTemp} ${unit}`
          : `--- ${unit}`;
      case 'heatingSchedule':
        return heatingScheduleList?.[0]?.inputTemp > 0
          ? `${heatingScheduleList[0].inputTemp} ${unit}`
          : `--- ${unit}`;
      case 'windFactor':
        return windFactor?.isActivated ? 'Active' : 'Inactive';
      case 'fanOnly':
        return fanOnly ? 'Running' : 'Stopped';
      default:
        return '--';
    }
  };

  // the old schedule controller shows the window either side of the temperature,
  // as `H : MM AM - D / M / YYYY`, and dashes until a schedule has been set
  const formatScheduleEdge = (edge) => {
    if (!edge?.date) return '-----------------';

    const date = edge.date instanceof Date ? edge.date : new Date(edge.date);
    return `${edge.time.hour} : ${edge.time.minute} ${edge.time.division} - ${date.getDate()} / ${
      date.getMonth() + 1
    } / ${date.getFullYear()}`;
  };

  const scheduleWindow = {
    start: formatScheduleEdge(heatingScheduleList?.[0]?.start),
    end: formatScheduleEdge(heatingScheduleList?.[0]?.end),
  };

  // the fan has its own lock in the old app and ignores the shared one
  const isProgramDisabled = (key) =>
    key === 'fanOnly' ? Boolean(isFanDisabled) : controlsDisabled;

  const handleToggle = (key, currentlyOn) => {
    if (isProgramDisabled(key)) return;

    if (currentlyOn) {
      integratedButtonHandler(key === 'optionalConstantTemp' ? 'constantTemp' : key, 'off');
      return;
    }

    if (key === 'instantHeat') integratedButtonHandler('instantHeat', 'tempA');
    else if (key === 'optionalConstantTemp') integratedButtonHandler('constantTemp', 'tempB');
    else if (key === 'snowSensor') integratedButtonHandler('snowSensor', 'on');
    else if (key === 'heatingSchedule') integratedButtonHandler('heatingSchedule', 'selectB');
    else if (key === 'windFactor') integratedButtonHandler('windFactor', 'on');
    else if (key === 'fanOnly') integratedButtonHandler('fanOnly', 'on');
  };

  const handleEdit = (key) => {
    if (controlsDisabled) return;

    if (key === 'instantHeat') integratedButtonHandler('instantHeat', 'tempA');
    else if (key === 'optionalConstantTemp') integratedButtonHandler('constantTemp', 'tempB');
    else if (key === 'heatingSchedule') integratedButtonHandler('heatingSchedule', 'selectB');
  };

  const activeProgramsCount = visiblePrograms.filter(({ key }) => getProgramActive(key)).length;
  const readyProgramsCount = visiblePrograms.filter(
    ({ key }) => !getProgramActive(key) && getCardState(key) === CARD_STATE.EDITING
  ).length;
  const modalTempLimits = getProgramTempLimits(editingProgramKey, isF);

  return (
    <>
      <Section>
        <SectionHeader>
          <HeaderCopy>
            <SectionTitle>ACTIVE PROGRAMS CONTROL</SectionTitle>
            <SectionText>
              Per-switch programs and staged values for the selected controller.
            </SectionText>
          </HeaderCopy>

          <HeaderStats>
            <CounterPill>{activeProgramsCount} of {visiblePrograms.length}</CounterPill>
            {readyProgramsCount > 0 && <ReadyPill>{readyProgramsCount} ready</ReadyPill>}
          </HeaderStats>
        </SectionHeader>

        <CardsStack>
          {visiblePrograms.map(({ key }) => {
            const meta = PROGRAM_META[key];
            const cardState = getCardState(key);
            // an armed-but-not-running program has to read as on or the toggle
            // would only ever re-arm it instead of cancelling
            const isOn =
              getProgramActive(key) ||
              (READY_MEANS_ARMED.includes(key) && Boolean(getProgramReady(key)));
            const tempValue = getTemp(key);
            const supportsEdit =
              key === 'instantHeat' ||
              key === 'optionalConstantTemp' ||
              key === 'heatingSchedule';

            return (
              <ProgramCard key={key} $state={cardState}>
                <CardTop>
                  <IconCircle $state={cardState}>
                    <IconImg src={meta.icon} alt={meta.label} />
                  </IconCircle>

                  <CardHeading>
                    <CardTitle>{meta.label}</CardTitle>
                    <StateRow>
                      <StateDot $state={cardState} />
                      <StateLabel>{STATE_LABEL[cardState]}</StateLabel>
                    </StateRow>
                  </CardHeading>

                  <ToggleTrack
                    type="button"
                    $on={isOn}
                    $state={cardState}
                    onClick={() => handleToggle(key, isOn)}
                    role="switch"
                    aria-checked={isOn}
                    disabled={isProgramDisabled(key)}
                  >
                    <ToggleThumb $on={isOn} />
                  </ToggleTrack>
                </CardTop>

                <ParamRow>
                  <ParamMeta>
                    <ParamLabel>{meta.paramLabel}</ParamLabel>
                    <ParamHint>{meta.hint}</ParamHint>
                  </ParamMeta>
                  <ParamValue>{tempValue}</ParamValue>
                </ParamRow>

                {key === 'heatingSchedule' && (
                  <>
                    <ParamRow>
                      <ParamMeta>
                        <ParamLabel>Start Date</ParamLabel>
                      </ParamMeta>
                      <ParamValue>{scheduleWindow.start}</ParamValue>
                    </ParamRow>
                    <ParamRow>
                      <ParamMeta>
                        <ParamLabel>End Date</ParamLabel>
                      </ParamMeta>
                      <ParamValue>{scheduleWindow.end}</ParamValue>
                    </ParamRow>
                  </>
                )}

                {supportsEdit && (
                  <EditButton
                    type="button"
                    $state={cardState}
                    onClick={() => handleEdit(key)}
                    disabled={isProgramDisabled(key)}
                  >
                    {meta.actionLabel}
                  </EditButton>
                )}
              </ProgramCard>
            );
          })}
        </CardsStack>
      </Section>

      {openMessageBox && createPortal(
        <MessageOverlay>
          <InputTempMessage
            onClose={() => setOpenMessageBox(false)}
            messages={message}
            title={programName}
            subtitle={'program control'}
            unitLabel={isF ? 'F' : 'C'}
            minTemp={modalTempLimits.min}
            maxTemp={modalTempLimits.max}
            onSubmitTemp={editingProgramKey ? (temp) => {
              const handlerKey = TEMP_PROGRAM_KEY_MAP[editingProgramKey] || editingProgramKey;
              integratedButtonHandler(handlerKey, 'on', temp);
              setOpenMessageBox(false);
            } : undefined}
          />
        </MessageOverlay>,
        document.body
      )}

      {hasConflictMessage && displayConflictMessage && createPortal(
        <MessageOverlay>
          <ConflictMessage
            currentSwitch={devicesConflicts?.currentSwitch}
            DesiredSwitch={devicesConflicts?.desiredSwitch}
            handleCancel={handleCancelConflictMessage}
            handleConfirm={() => handleConfirmConflictMessage(devicesConflicts)}
          />
        </MessageOverlay>,
        document.body
      )}

      {showScheduleEditor && createPortal(
        <ScheduleOverlay>
          <ScheduleEditorPanel>
            <ScheduleCalendar
              scheduleList={heatingScheduleList?.[0] || {
                start: { date: null, time: null },
                end: { date: null, time: null },
              }}
              handleScheduler={(data) => {
                setScheduleData(data);
              }}
              handleClose={() => {
                setShowScheduleEditor(false);
                setScheduleData(null);
                setScheduleTempInput('');
              }}
              handleClear={() => {
                setScheduleData(null);
              }}
            />
            <ScheduleTempRow>
              <ScheduleTempLabel>Temperature ({isF ? '°F' : '°C'})</ScheduleTempLabel>
              <ScheduleTempInput
                type="number"
                placeholder={isF ? '250 – 1830' : '121 – 999'}
                value={scheduleTempInput}
                onChange={(e) => setScheduleTempInput(e.target.value)}
              />
              <ScheduleApplyButton
                type="button"
                disabled={!scheduleData?.start?.date || !scheduleData?.end?.date || !scheduleTempInput}
                onClick={() => {
                  const temp = Number(scheduleTempInput);
                  if (!temp) return;
                  integratedButtonHandler('heatingSchedule', 'on', temp, scheduleData, 0);
                  setShowScheduleEditor(false);
                  setScheduleData(null);
                  setScheduleTempInput('');
                }}
              >
                SET SCHEDULE
              </ScheduleApplyButton>
            </ScheduleTempRow>
          </ScheduleEditorPanel>
        </ScheduleOverlay>,
        document.body
      )}
    </>
  );
};

export default SwitchProgramCards;
const applyCardState = (state) => {
  if (state === CARD_STATE.EDITING) return cardEditing;
  if (state === CARD_STATE.ACTIVE) return cardActive;
  if (state === CARD_STATE.FAULT) return cardFault;
  return cardDefault;
};

const applyIconState = (state) => {
  if (state === CARD_STATE.EDITING) return iconContainerEditing;
  if (state === CARD_STATE.ACTIVE) return iconContainerActive;
  if (state === CARD_STATE.FAULT) return iconContainerFault;
  return iconContainerDefault;
};

const applyButtonState = (state) => {
  if (state === CARD_STATE.EDITING) return cardButtonEditing;
  if (state === CARD_STATE.ACTIVE) return cardButtonActive;
  if (state === CARD_STATE.FAULT) return cardButtonFault;
  return cardButtonDefault;
};

const Section = styled.section`
  background: var(--switch-modal-bg);
  border: 1px solid var(--switch-border-strong);
  border-radius: ${radiusSection};
  overflow: hidden;
  backdrop-filter: blur(12px);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 8px;
  border-bottom: 1px solid var(--switch-border);

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--switch-text-faint);
`;

const SectionText = styled.p`
  margin: 0;
  max-width: 240px;
  color: var(--switch-text-muted);
  font-size: 11px;
  line-height: 1.5;
`;

const CounterPill = styled.span`
  min-width: 68px;
  height: 28px;
  padding: 0 10px;
  border-radius: ${radiusPill};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.14);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  white-space: nowrap;
`;

const ReadyPill = styled.span`
  min-width: 64px;
  height: 28px;
  padding: 0 10px;
  border-radius: ${radiusPill};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  white-space: nowrap;
`;

const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px 20px;
`;

const ProgramCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: ${radiusCard};
  border: 1px solid var(--switch-border);
  transition: all 0.25s ease;
  ${({ $state }) => applyCardState($state)};

  ${({ $state }) =>
    $state === CARD_STATE.ACTIVE &&
    css`
      border-color: rgba(20, 207, 32, 0.3);
      box-shadow: inset 0 0 40px rgba(62, 192, 21, 0.08);
    `}

  ${({ $state }) =>
    $state === CARD_STATE.EDITING &&
    css`
      border-color: rgba(59, 130, 246, 0.4);
      box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.12);
    `}

  ${({ $state }) =>
    $state === CARD_STATE.FAULT &&
    css`
      border-color: rgba(238, 75, 86, 0.3);
      box-shadow: inset 0 0 40px rgba(238, 75, 86, 0.06);
    `}
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $state }) => applyIconState($state)};
`;

const IconImg = styled.img`
  width: 18px;
  height: 18px;
`;

const CardHeading = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--switch-text-primary);
  letter-spacing: -0.2px;
`;

const StateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StateDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $state }) => STATE_ACCENT[$state]};
  box-shadow: ${({ $state }) => `0 0 8px ${STATE_ACCENT[$state]}40`};
`;

const StateLabel = styled.span`
  color: var(--switch-text-muted);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const ToggleTrack = styled.button`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s, opacity 0.2s ease;
  background: ${({ $on, $state }) =>
    $on ? ($state === CARD_STATE.FAULT ? '#ee4b56' : '#3ec015') : 'var(--switch-control-track)'};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 3px;
  left: ${({ $on }) => ($on ? '21px' : '3px')};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--switch-panel-bg);
  transition: left 0.2s;
`;

const ParamRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: ${radiusCardInner};
  ${cardInnerArea};
  border: 1px solid var(--switch-border-soft);
`;

const ParamMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const ParamLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--switch-text-muted);
`;

const ParamHint = styled.span`
  color: var(--switch-text-faint);
  font-size: 10px;
  line-height: 1.5;
`;

const ParamValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--switch-text-primary);
  white-space: nowrap;
`;

const EditButton = styled.button`
  width: 100%;
  padding: 11px 14px;
  border-radius: ${radiusCardInner};
  border: none;
  ${({ $state }) => applyButtonState($state)};
  color: ${({ $state }) => ($state === CARD_STATE.DEFAULT ? 'var(--switch-text-secondary)' : '#ffffff')};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    filter: brightness(1.06);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    filter: none;
  }
`;

const MessageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--switch-overlay-bg);
  z-index: 1000;
`;

const ScheduleOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--switch-overlay-strong-bg);
  z-index: 9999;
  overflow: auto;
`;

const ScheduleEditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1001;
`;

const ScheduleTempRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 12px;
  background: var(--switch-modal-bg);
  border: 1px solid var(--switch-border-strong);
`;

const ScheduleTempLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--switch-text-muted);
  white-space: nowrap;
`;

const ScheduleTempInput = styled.input`
  width: 140px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #3b82f6;
  background: var(--switch-modal-bg);
  color: var(--switch-text-primary);
  font-size: 14px;
  padding: 0 10px;
  text-align: center;
  outline: none;

  &::placeholder {
    color: var(--switch-text-faint);
    font-size: 12px;
  }

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
  }
`;

const ScheduleApplyButton = styled.button`
  height: 32px;
  padding: 0 16px;
  border-radius: 6px;
  border: none;
  background: #3b82f6;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;
